import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { ArrowRight, X, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import RankTrustLogo from "@/components/RankTrustLogo";
import SEOHead from "@/lib/seo/SEOHead";

export default function Onboarding() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", category: "", address: "", city: "", state: "", zip_code: "",
    phone: "", website: "", description: "",
    lat: 30.2672, lng: -97.7431,
  });
  const [keyword, setKeyword] = useState("");
  const [keywords, setKeywords] = useState([]);

  const addKw = () => {
    if (!keyword.trim()) return;
    if (keywords.includes(keyword.trim())) return;
    setKeywords([...keywords, keyword.trim()]);
    setKeyword("");
  };
  const removeKw = (k) => setKeywords(keywords.filter((x) => x !== k));

  const finish = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/businesses", { ...form, keywords });
      localStorage.setItem("active_business_id", data.id);
      toast.success("Business created");
      nav("/app");
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <SEOHead path="/onboarding" />
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <RankTrustLogo height={32} asLink tagline />
          <div className="font-mono text-xs text-zinc-500">STEP {step} OF 3</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <div className="grid grid-cols-3 gap-1 mb-12">
          {[1,2,3].map((s) => (
            <div key={s} className={`h-1 ${s <= step ? "bg-[#002FA7]" : "bg-zinc-200"}`}></div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-6" data-testid="onb-step-1">
            <div>
              <div className="label-tiny mb-2">Step 1</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">Tell us about your business</h1>
              <p className="text-zinc-600 mt-2">We use this to seed your audit and rank tracking. Read-only mode — we won't post anything to your GBP yet.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Business name *" testid="onb-name">
                <input className="input-field" required value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} />
              </Field>
              <Field label="Primary category *" testid="onb-category">
                <input className="input-field" placeholder="e.g. Plumber" required value={form.category} onChange={(e)=>setForm({...form,category:e.target.value})} />
              </Field>
              <Field label="Phone" testid="onb-phone">
                <input className="input-field" value={form.phone} onChange={(e)=>setForm({...form,phone:e.target.value})} />
              </Field>
              <Field label="Website" testid="onb-website">
                <input className="input-field" value={form.website} onChange={(e)=>setForm({...form,website:e.target.value})} />
              </Field>
              <div className="md:col-span-2">
                <Field label="Description" testid="onb-desc">
                  <textarea className="input-field" rows={3} value={form.description} onChange={(e)=>setForm({...form,description:e.target.value})} />
                </Field>
              </div>
            </div>
            <button className="btn-primary" onClick={()=>setStep(2)} disabled={!form.name || !form.category} data-testid="onb-next-1">
              Next <ArrowRight size={14} weight="bold" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6" data-testid="onb-step-2">
            <div>
              <div className="label-tiny mb-2">Step 2</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">Where are you located?</h1>
              <p className="text-zinc-600 mt-2">We need this to track your rank from real geo-points around your service area.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
              <div className="md:col-span-6"><Field label="Address" testid="onb-addr"><input className="input-field" value={form.address} onChange={(e)=>setForm({...form,address:e.target.value})} /></Field></div>
              <div className="md:col-span-3"><Field label="City *" testid="onb-city"><input className="input-field" required value={form.city} onChange={(e)=>setForm({...form,city:e.target.value})} /></Field></div>
              <div className="md:col-span-2"><Field label="State *" testid="onb-state"><input className="input-field" required value={form.state} onChange={(e)=>setForm({...form,state:e.target.value})} /></Field></div>
              <div className="md:col-span-1"><Field label="Zip *" testid="onb-zip"><input className="input-field" required value={form.zip_code} onChange={(e)=>setForm({...form,zip_code:e.target.value})} /></Field></div>
              <div className="md:col-span-3"><Field label="Latitude" testid="onb-lat"><input className="input-field" type="number" step="any" value={form.lat} onChange={(e)=>setForm({...form,lat:parseFloat(e.target.value)})} /></Field></div>
              <div className="md:col-span-3"><Field label="Longitude" testid="onb-lng"><input className="input-field" type="number" step="any" value={form.lng} onChange={(e)=>setForm({...form,lng:parseFloat(e.target.value)})} /></Field></div>
            </div>
            <div className="flex gap-3">
              <button className="btn-secondary" onClick={()=>setStep(1)}>Back</button>
              <button className="btn-primary" onClick={()=>setStep(3)} disabled={!form.city || !form.state || !form.zip_code} data-testid="onb-next-2">
                Next <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6" data-testid="onb-step-3">
            <div>
              <div className="label-tiny mb-2">Step 3</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">Which keywords do you want to rank for?</h1>
              <p className="text-zinc-600 mt-2">Add 3–10 high-intent keywords to start. We'll track them across the geo-grid.</p>
            </div>
            <div className="flex gap-2">
              <input className="input-field flex-1" placeholder="e.g. plumber near me" value={keyword} onChange={(e)=>setKeyword(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&(e.preventDefault(),addKw())} data-testid="onb-keyword-input" />
              <button className="btn-dark" onClick={addKw} data-testid="onb-keyword-add"><Plus size={14} weight="bold" /> Add</button>
            </div>
            <div className="flex flex-wrap gap-2 min-h-[3rem]">
              {keywords.map((k) => (
                <span key={k} className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-100 text-sm font-mono" data-testid={`kw-chip-${k}`}>
                  {k}
                  <button onClick={()=>removeKw(k)} className="text-zinc-500 hover:text-zinc-900"><X size={12} weight="bold" /></button>
                </span>
              ))}
              {!keywords.length && <span className="text-zinc-400 text-sm font-mono">No keywords yet</span>}
            </div>
            <div className="flex gap-3">
              <button className="btn-secondary" onClick={()=>setStep(2)}>Back</button>
              <button className="btn-primary" onClick={finish} disabled={busy || !keywords.length} data-testid="onb-finish">
                {busy ? <span className="spinner"></span> : <>Launch dashboard <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block space-y-2" data-testid={testid}>
      <span className="label-tiny block">{label}</span>
      {children}
    </label>
  );
}
